import { LogsEvent, datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { config } from 'config'

interface ErrorFilter {
  name: string
  test: (message: string) => boolean
}

const ddApplicationID = process.env.REACT_APP_DD_RUM_APPLICATION_ID
const ddClientToken = process.env.REACT_APP_DD_RUM_CLIENT_TOKEN
const environment =
  process.env.REACT_APP_DD_RUM_ENVIRONMENT ||
  window?.location?.hostname ||
  process.env.NODE_ENV

const REACT_APP_API_URL = config.urls.api
const REACT_APP_ATLAS_URL = config.urls.atlas

function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&') // $& means the whole matched string
}

export const logger = datadogLogs.createLogger('ddLogger', {
  handler: 'console',
})

const errorFilters: ErrorFilter[] = [
  {
    name: 'EmailLinkValidation',
    test: (message) =>
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
        message,
      ),
  },
]

const initializeDatadogRUM = () => {
  if (environment && ddApplicationID && ddClientToken) {
    const ddConfig = {
      applicationId: ddApplicationID,
      clientToken: ddClientToken,
      datacenter: 'us',
      env: environment,
      service: 'joor-react',
      sampleRate: 100,
      replaySampleRate: 0,
      silentMultipleInit: true,
      trackSessionAcrossSubdomains: true,
      version: process.env.REACT_APP_VERSION,
    }
    const allowedTracingOrigins = []
    if (REACT_APP_ATLAS_URL !== undefined) {
      allowedTracingOrigins.push(new URL(REACT_APP_ATLAS_URL).origin)
    }
    if (REACT_APP_API_URL !== undefined) {
      allowedTracingOrigins.push(
        new RegExp(
          escapeRegExp('https://') +
            '.*' +
            escapeRegExp(
              REACT_APP_API_URL.slice(
                REACT_APP_API_URL.indexOf(environment) + environment.length,
              ),
            ),
        ),
      )
    }
    datadogRum.init({
      ...ddConfig,
      allowedTracingOrigins,
      // Override sampleRate only for datadogRUM
      sampleRate: 10,
      beforeSend: (event, context) => {
        event.context = {
          ...event.context,
          response:
            'response' in context && context.response
              ? {
                  headers: context.response.headers,
                  body: context.response.body,
                  status: context.response.status,
                }
              : undefined,
        }
      },
    })

    datadogLogs.init({
      ...ddConfig,
      forwardErrorsToLogs: true,
      // @ts-expect-error https://github.com/DataDog/browser-sdk/issues/1413
      forwardConsoleLogs: 'error',
      // Filter out error that might be caused by link validation/ pre-fetch rendering from email clicks
      // https://github.com/DataDog/browser-sdk/issues/2715
      beforeSend: (event: LogsEvent) => {
        if (event?.message && typeof event.message === 'string') {
          const msg = event.message
          for (const filter of errorFilters) {
            if (filter.test(msg)) {
              return false
            }
          }
        }
        return true
      },
    })
    if (process.env.NODE_ENV !== 'development') {
      logger.setHandler('http')
    }
  }
}

export const setLocationForDatadogRUM = (location: any) => {
  datadogRum.addRumGlobalContext('location', location)
}

export const setUserForDatadog = ({
  userId,
  account,
}: {
  userId: string
  account: { accountId: string }
}) => {
  if (environment && ddApplicationID && ddClientToken) {
    datadogRum.addRumGlobalContext('user', {
      userId: userId,
      accountId: account.accountId,
    })
    datadogLogs.addLoggerGlobalContext('user', {
      userId: userId,
      accountId: account.accountId,
    })
  }
}

export default initializeDatadogRUM
